import React from 'react';
import s from './s.module.less';
import useGetPPAPaymentInfo from 'hooks/useGetPPAPaymentInfo';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { Button, Modal, Spin } from 'antd';
import EFaxLandingPage from './EFaxLandingPage';
import EFaxDashboardPage from './EFaxDashboardPage';
import EFaxSetUpPage from './EFaxSetUpPage';
import TransferSubmitted from './EFaxSetUpPage/TransferFlow/TransferSubmitted';
import useFaxNumber from 'hooks/useFaxNumber';
import HomeStore from 'store/Home';
import { EPayProviderListType } from 'types/provider';
import { setHasHideFaxNumberBanner } from 'utils/localstore';
import { EFAX_USER_CANCELED_SESSION_KEY, EFAX_PAY_PENDING_SETUP_KEY } from 'utils/eFaxSubscriptionStatus';
import commonS from 'styles/common.module.less';
import setupS from './EFaxSetUpPage/s.module.less';

const LEGACY_HAS_PAID_SESSION_KEY = 'hasPaidEFax';
const EFAX_FORCE_SETUP_AFTER_PAY_KEY = 'EFAX_FORCE_SETUP_AFTER_PAY';

const EFaxPage = () => {
    const [showReenableLanding, setShowReenableLanding] = React.useState(false);
    const [paymentInfo, , , refetchPaymentInfo] = useGetPPAPaymentInfo();
    const [homeInfo, loadingHome] = useProviderHomeInfo();
    const [getHomeStore] = HomeStore.useStore();
    const homeFromStore = getHomeStore('data');
    const [faxNumber, loadingFaxNumber, refetchFaxNumber, { hasCancelledAccount, pendingPortFaxNumber }] =
        useFaxNumber();
    const [showLearnMore, setShowLearnMore] = React.useState(false);

    // Hook state can lag behind Layout / HomeStore; either source having KLARITY_E_FAX means no re-purchase.
    const hasEfaxPayProvider = Boolean(
        homeInfo?.payProviderList?.includes(EPayProviderListType.KLARITY_E_FAX) ||
        homeFromStore?.payProviderList?.includes(EPayProviderListType.KLARITY_E_FAX),
    );

    // No activeAccount: cancelled + KLARITY_E_FAX in home → 选号; cancelled + no KLARITY_E_FAX → 黄条 Dashboard.
    const hasActiveFax = Boolean(faxNumber);

    const [payPendingSetup, setPayPendingSetup] = React.useState(
        () => sessionStorage.getItem(EFAX_PAY_PENDING_SETUP_KEY) === '1',
    );

    const [forceSetupAfterPay, setForceSetupAfterPay] = React.useState(
        () => sessionStorage.getItem(EFAX_FORCE_SETUP_AFTER_PAY_KEY) === '1',
    );

    const showPendingPortPage =
        Boolean(pendingPortFaxNumber) &&
        !hasActiveFax;

    const showPurchaseLanding =
        !showPendingPortPage && !hasActiveFax && !hasCancelledAccount && !payPendingSetup && !hasEfaxPayProvider;

    const showSetupPage =
        !showPendingPortPage &&
        // After a successful (re)purchase we force user into number setup flow.
        // This also handles the case where backend still returns an existing `faxNumber`
        // immediately after cancel/re-purchase (we still need to land on 选号界面).
        (forceSetupAfterPay ||
            (!hasActiveFax &&
                // After a successful (re)purchase we set `payPendingSetup` to bridge any delay
                // before `homeInfo` reflects `KLARITY_E_FAX`. This ensures we go straight to
                // number setup instead of showing the canceled (yellow-banner) dashboard.
                (hasEfaxPayProvider || payPendingSetup)));

    /** Dashboard: 有号码，或 已取消且未订阅 eFax（黄条） */
    const showDashboardPage =
        !showPendingPortPage &&
        !forceSetupAfterPay &&
        (hasActiveFax || (!hasActiveFax && hasCancelledAccount && !hasEfaxPayProvider && !payPendingSetup));

    React.useEffect(() => {
        if (sessionStorage.getItem(LEGACY_HAS_PAID_SESSION_KEY) === 'true' && !faxNumber) {
            sessionStorage.setItem(EFAX_PAY_PENDING_SETUP_KEY, '1');
            sessionStorage.removeItem(LEGACY_HAS_PAID_SESSION_KEY);
            setPayPendingSetup(true);
        }
    }, [faxNumber]);

    React.useEffect(() => {
        if (faxNumber) {
            sessionStorage.removeItem(EFAX_PAY_PENDING_SETUP_KEY);
            setPayPendingSetup(false);
            setShowReenableLanding(false);
        }
    }, [faxNumber]);

    const loadingRouting = loadingFaxNumber || (loadingHome && !homeInfo && !homeFromStore);
    if (loadingRouting) {
        return <Spin />;
    }

    return (
        <div className={s.wrap}>
            {showPendingPortPage && (
                <div className={setupS.wrap}>
                    <div className={setupS.titleBar}>
                        <div className={setupS.pageTitle}>eFax</div>
                        <Button onClick={() => setShowLearnMore(true)} className={setupS.learnMoreBtn}>
                            Learn more
                        </Button>
                    </div>
                    <div className={setupS.body}>
                        <TransferSubmitted transferNumber={pendingPortFaxNumber!} />
                    </div>
                    <Modal
                        open={showLearnMore}
                        onCancel={() => setShowLearnMore(false)}
                        footer={null}
                        width="84%"
                        className={commonS.modalFixHeightWithoutPaddingWrap}
                        title="Learn more about eFax"
                    >
                        <EFaxLandingPage isModal />
                    </Modal>
                </div>
            )}
            {showPurchaseLanding && (
                <EFaxLandingPage
                    paymentInfo={paymentInfo}
                    onPaymentInfoChange={refetchPaymentInfo}
                    onPaid={() => {
                        // Ensure success (green) banner shows after (re)purchase + setup.
                        setHasHideFaxNumberBanner(false);
                        sessionStorage.setItem(EFAX_FORCE_SETUP_AFTER_PAY_KEY, '1');
                        setForceSetupAfterPay(true);
                        sessionStorage.setItem(EFAX_PAY_PENDING_SETUP_KEY, '1');
                        setPayPendingSetup(true);
                        refetchFaxNumber();
                    }}
                />
            )}
            {showSetupPage && (
                <EFaxSetUpPage
                    onSetUp={() => {
                        sessionStorage.removeItem(EFAX_FORCE_SETUP_AFTER_PAY_KEY);
                        setForceSetupAfterPay(false);
                        refetchFaxNumber();
                    }}
                />
            )}
            {showDashboardPage && showReenableLanding && (
                <EFaxLandingPage
                    paymentInfo={paymentInfo}
                    onPaymentInfoChange={refetchPaymentInfo}
                    onPaid={() => {
                        sessionStorage.removeItem(EFAX_USER_CANCELED_SESSION_KEY);
                        // Ensure success (green) banner shows after re-enabling + setup.
                        setHasHideFaxNumberBanner(false);
                        sessionStorage.setItem(EFAX_FORCE_SETUP_AFTER_PAY_KEY, '1');
                        setForceSetupAfterPay(true);
                        sessionStorage.setItem(EFAX_PAY_PENDING_SETUP_KEY, '1');
                        setPayPendingSetup(true);
                        setShowReenableLanding(false);
                        refetchFaxNumber();
                    }}
                />
            )}
            {showDashboardPage && !showReenableLanding && (
                <EFaxDashboardPage
                    onGetStarted={() => {
                        // 黄条场景（无 KLARITY_E_FAX）：介绍页后再开通。
                        setShowReenableLanding(true);
                    }}
                />
            )}
        </div>
    );
};

export default EFaxPage;
